/**
 * QueueSheet — the queue, in a sheet from the bottom edge: what plays after what, and the
 * four things a listener does with it.
 *
 * Task 08, checklist item «Экран очереди: список, перетаскивание для смены порядка, удаление,
 * кнопка „очистить“», and the modes: «Перемешать» and «Повтор». Opened from the mini player,
 * so it is one tap away on every tab. A sheet, like the track card, for the card's reason:
 * the screen behind keeps its place.
 *
 * ## The list is the listener's order, even while shuffled
 *
 * The rows are the queue as it stands; the shuffled order is computed from them and is not
 * drawn (`store/queue.ts`). Dragging reorders the list — which is what the listener sees and
 * what turning shuffle off plays — and a line under the modes says the tracks play
 * «вразброс» while shuffle is on, so the list is not mistaken for the order of play.
 *
 * ## Dragging is measured in rows, not in pixels of a layout
 *
 * Every row is {@link QUEUE_ROW_HEIGHT} tall and every label over a part of the list
 * {@link QUEUE_LABEL_HEIGHT}, so where a dragged row lands is worked out from those declared
 * heights ({@link dropPosition}): a drag of N rows is a move of N places, a label crossed on the
 * way counts for nothing. Nothing is measured on screen: a drag works the same in a test without
 * layout and on a phone. The handle takes the pointer and holds it (`setPointerCapture`), so a drag
 * that leaves the handle is still the handle's; `touch-action: none` on it keeps the list
 * from scrolling under the thumb. A cancelled drag — a call, the system taking the gesture —
 * changes nothing. The same move is on the keyboard: the handle is a real button, and the
 * arrows move a track by one place.
 *
 * ## It opens at the top, on the track playing
 *
 * «Закрыть» sits in the header, above the list, and takes the focus on open — the track card
 * puts its own at the bottom, which in a sheet of a few hundred rows would scroll the sheet to
 * its last row the moment focus landed there (found in a real browser, `raw/zadacha-08-proverka.md`).
 * Then the row playing is scrolled into view: after an hour of a long queue it is far below
 * the top.
 *
 * ## Pinned rows say «следующим» (task 44)
 *
 * A track put there by «Играть следующим» and not yet played carries the word under its title:
 * under shuffle the list is the listener's order and says nothing of the order of play, and these
 * are the tracks that will come next anyway (`store/queue.ts`, `pinsAhead`).
 *
 * ## «Переход» beside them (task 45)
 *
 * The third mode: how one track goes into the next — «встык», «наложение» (the default) or «микс»
 * (`engine/player.ts`, «Two decks»). A chip like «Повтор», saying the way it is in; a tap walks
 * to the next. Lit while it is anything but «встык», the way a switch shows it does something.
 * The row wraps (`styles/queue.css`), so a fourth chip on an iPhone SE goes under, not off-screen.
 *
 * ## The sleep timer beside them (task 48)
 *
 * «Таймер сна» opens a row of four under the modes — 15, 30, 60 minutes, to the end of the track —
 * and a choice folds it away (`spec.md` stories 134–136). On, the same chip says what is left and
 * how it ends («Сон 14:59 ✕»), lit, and one tap turns it off: a chip that walked through the four
 * like «Повтор» would take four taps to be off. What is left is the player's to count
 * (`engine/player.ts`, «The sleep timer»); the sheet only draws it.
 *
 * ## The same list on the pult's screen (task 54)
 *
 * {@link QueueBody} is the sheet without its frame — the modes and the list — and the pult's screen
 * QUEUE draws it in the middle of the board. There a tap on a row chooses the track for LOAD
 * instead of playing it (`onPick`), and the row chosen (`pickedId`) is lit.
 *
 * ## The bill's sheet (task 71)
 *
 * From 96 points under the top: «Очередь» in gold, how many and how long («60 треков · 3 ч 41 мин»),
 * the cross in the corner; five buttons with an icon over a word — «Перемешать», «Повтор»,
 * «Наложение», «Сон», «Очистить» — whose word says the mode when it is on («Повтор: один»,
 * «Сон 29:59») while a screen reader hears the whole («Повтор: один трек»); and the list under
 * «Сейчас» and «Дальше». Tracks already played stay above «Сейчас», with no label: a listener goes
 * back to them with «предыдущий».
 *
 * ## What cannot be done here, on purpose
 *
 * The track playing has no «Убрать» (skipping it is «следующий»), and «Очистить» keeps it:
 * the player holds exactly the current entry of the queue.
 */

import type { JSX, KeyboardEvent, PointerEvent } from 'react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { player } from '../engine/player';
import type { SleepChoice } from '../engine/player';
import type { TrackItem } from '../lib/api';
import { formatCountdown, formatDuration, formatSpan, trackCount } from '../lib/format';
import { isSounding, usePlayer } from '../store/player';
import type { RepeatMode, TransitionMode } from '../store/queue';
import { pinsAhead, useQueue } from '../store/queue';
import '../styles/queue.css';

import {
  CloseGlyph,
  FadeGlyph,
  GrabGlyph,
  MoonGlyph,
  RepeatGlyph,
  ShuffleGlyph,
  TrashGlyph,
} from './KeyIcons';
import { LevelBars } from './TrackRow';

/** Pixels: a row of the sheet, `--queue-row-h`. */
export const QUEUE_ROW_HEIGHT = 60;
/** Pixels: «Сейчас» or «Дальше» over a part of the list, `--queue-label-h`. */
export const QUEUE_LABEL_HEIGHT = 36;

/**
 * Task 71: where a row dragged by `offsetPx` from place `from` lands, among `count` rows with the
 * current one at `index` — the labels «Сейчас» (over the current row) and «Дальше» (over the one
 * after it) counted in at their heights. The row lands past every row whose middle its own middle
 * has gone by; with no labels on the way, that is `from` plus the rows dragged, rounded.
 */
export function dropPosition(count: number, index: number, from: number, offsetPx: number): number {
  const middles: number[] = [];
  let y = 0;
  for (let position = 0; position < count; position += 1) {
    if (position === index || (index >= 0 && position === index + 1)) y += QUEUE_LABEL_HEIGHT;
    middles.push(y + QUEUE_ROW_HEIGHT / 2);
    y += QUEUE_ROW_HEIGHT;
  }
  const moved = (middles[from] ?? 0) + offsetPx;
  // A row above is passed once the middle has gone beyond its middle, a row below once it has
  // reached it: a drag of exactly N rows is a move of N places, either way.
  return middles.filter(
    (middle, position) => position !== from && (position < from ? middle < moved : middle <= moved),
  ).length;
}

export const QUEUE_TITLE = 'Очередь';
/** Task 71: the labels over the track playing and over what follows it. */
export const NOW_LABEL = 'Сейчас';
export const NEXT_LABEL_TEXT = 'Дальше';
export const QUEUE_LIST_LABEL = 'Треки в очереди';
export const CLEAR_LABEL = 'Очистить';
export const SHUFFLE_LABEL = 'Перемешать';
export const CLOSE_LABEL = 'Закрыть';
export const SHUFFLE_NOTE_TEXT =
  'Треки играют вразброс. Список ниже — ваш порядок без перемешивания.';
export const EMPTY_QUEUE_TEXT = 'Очередь пуста. Нажмите трек в списке — очередь соберётся из него.';
/** Task 44: under the title of a track put there by «Играть следующим». */
export const PINNED_TEXT = 'следующим';

/**
 * What a screen reader hears of the repeat button: the mode it is in, not the one a tap would
 * switch to. Task 71: the word under its icon is shorter ({@link REPEAT_KEY_TEXT}).
 */
export const REPEAT_TEXT: Readonly<Record<RepeatMode, string>> = Object.freeze({
  off: 'Повтор: выключен',
  all: 'Повтор: весь список',
  one: 'Повтор: один трек',
});

/** Task 71: the word under the repeat button's icon — its name, and the mode when it is on. */
export const REPEAT_KEY_TEXT: Readonly<Record<RepeatMode, string>> = Object.freeze({
  off: 'Повтор',
  all: 'Повтор: все',
  one: 'Повтор: один',
});

/** Task 48: the button that opens the sleep timer's choice; task 71: «Сон», as on the picture. */
export const SLEEP_LABEL = 'Сон';

/** Task 48: the four choices, in the order they are offered. */
export const SLEEP_CHOICES: readonly SleepChoice[] = Object.freeze([15, 30, 60, 'track']);

/** Task 48: what each choice says. */
export const SLEEP_CHOICE_TEXT: Readonly<Record<SleepChoice, string>> = Object.freeze({
  15: '15 мин',
  30: '30 мин',
  60: '60 мин',
  track: 'До конца трека',
});

/** Task 48: what a screen reader hears of the chip while the timer is on — and what a tap does. */
export function sleepOffLabel(left: string | undefined): string {
  return left === undefined ? 'Выключить таймер сна' : `Выключить таймер сна, осталось ${left}`;
}

/** Task 45: what a screen reader hears of the transition button — the way it is in. */
export const TRANSITION_TEXT: Readonly<Record<TransitionMode, string>> = Object.freeze({
  cut: 'Переход: встык',
  blend: 'Переход: наложение',
  mix: 'Переход: микс',
});

/**
 * Task 71: the word under its icon. Off — «встык» — it is the button's name, «Наложение»; on, the
 * way it is in.
 */
export const TRANSITION_KEY_TEXT: Readonly<Record<TransitionMode, string>> = Object.freeze({
  cut: 'Наложение',
  blend: 'Наложение',
  mix: 'Микс',
});

export function removeLabel(title: string): string {
  return `Убрать «${title}»`;
}

export function moveLabel(title: string): string {
  return `Переместить «${title}»`;
}

/** A drag in progress. In a ref for the handlers, mirrored in state for the drawing. */
interface Drag {
  readonly key: number;
  readonly pointerId: number;
  readonly startY: number;
  readonly from: number;
  to: number;
}

export interface QueueSheetProps {
  readonly onClose: () => void;
}

export function QueueSheet({ onClose }: QueueSheetProps): JSX.Element {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const count = useQueue((s) => s.entries.length);
  const totalS = useQueue((s) => s.entries.reduce((sum, entry) => sum + entry.track.durationS, 0));

  // A layout effect: «Закрыть» takes the focus before the body scrolls the row playing into view.
  useLayoutEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <>
      <div className="sheetBackdrop" aria-hidden="true" onClick={onClose} />
      <section
        className="sheet queueSheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="sheetGrab" aria-hidden="true" />
        <header className="queueHead">
          <button
            ref={closeRef}
            type="button"
            className="sheetClose"
            aria-label={CLOSE_LABEL}
            onClick={onClose}
          >
            <CloseGlyph className="keyIcon" />
          </button>
          <h2 id={titleId} className="queueHeading">
            {QUEUE_TITLE}
          </h2>
          <p className="queueCount">
            {count > 0 ? `${trackCount(count)} · ${formatSpan(totalS)}` : ''}
          </p>
        </header>
        <QueueBody />
      </section>
    </>
  );
}

/**
 * Task 54: what a tap on a row does instead of playing it, and the row chosen that way — the
 * pult's screen chooses a track for LOAD with the same list. Absent, a tap plays, as in the sheet.
 */
export interface QueueBodyProps {
  readonly onPick?: ((track: TrackItem) => void) | undefined;
  readonly pickedId?: number | undefined;
}

/** The sheet without its frame: the modes and the list (task 54, the pult's screen QUEUE). */
export function QueueBody({ onPick, pickedId }: QueueBodyProps = {}): JSX.Element {
  const entries = useQueue((s) => s.entries);
  const index = useQueue((s) => s.index);
  const shuffle = useQueue((s) => s.shuffle);
  const repeat = useQueue((s) => s.repeat);
  const transition = useQueue((s) => s.transition);
  const playing = usePlayer((s) => s.status === 'playing');
  const sleepAt = usePlayer((s) => s.sleepAt);
  const sleepLeftS = usePlayer((s) => s.sleepLeftS);
  const [choosingSleep, setChoosingSleep] = useState(false);
  const sleepLeft = sleepLeftS === undefined ? undefined : formatCountdown(sleepLeftS);
  // A string, so an edit that leaves the same rows pinned re-renders nothing for it.
  const pinnedKeys = useQueue((s) => pinsAhead(s).join(','));
  const pinned = new Set(pinnedKeys === '' ? [] : pinnedKeys.split(',').map(Number));

  const currentRef = useRef<HTMLLIElement | null>(null);
  const handles = useRef(new Map<number, HTMLButtonElement>());
  /** The entry whose handle moved by an arrow key: its handle gets the focus back. */
  const refocus = useRef<number | undefined>(undefined);
  const drag = useRef<Drag | undefined>(undefined);
  const [dragging, setDragging] = useState<{ key: number; offset: number; to: number }>();

  useEffect(() => {
    // `scrollIntoView` is absent where there is no layout; the sheet then simply starts at the top.
    currentRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);

  useEffect(() => {
    // React keeps the row's element through a reorder, but moving a focused element in the
    // document can drop its focus in a browser; the arrow keys must be pressable again at once.
    const key = refocus.current;
    refocus.current = undefined;
    if (key !== undefined) handles.current.get(key)?.focus();
  }, [entries]);

  const nothingToClear = entries.every((_, position) => position === index);

  /* ── dragging ─────────────────────────────────────────────────────────────── */

  function startDrag(event: PointerEvent<HTMLButtonElement>, key: number, from: number): void {
    // One drag at a time: a second finger is ignored, not allowed to replace the first.
    if (drag.current !== undefined) return;
    drag.current = { key, pointerId: event.pointerId, startY: event.clientY, from, to: from };
    setDragging({ key, offset: 0, to: from });
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A webview without pointer capture: the drag still works while the finger stays on
      // the handle.
    }
  }

  function moveDrag(event: PointerEvent<HTMLButtonElement>): void {
    const current = drag.current;
    if (current?.pointerId !== event.pointerId) return;
    const offset = event.clientY - current.startY;
    const to = dropPosition(entries.length, index, current.from, offset);
    current.to = to;
    setDragging({ key: current.key, offset, to });
  }

  function endDrag(pointerId: number, commit: boolean): void {
    const current = drag.current;
    if (current?.pointerId !== pointerId) return;
    drag.current = undefined;
    setDragging(undefined);
    if (current === undefined || !commit || current.to === current.from) return;
    useQueue.getState().move(current.key, current.to);
  }

  function keyMove(event: KeyboardEvent<HTMLButtonElement>, key: number, from: number): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    refocus.current = key;
    useQueue.getState().move(key, event.key === 'ArrowUp' ? from - 1 : from + 1);
  }

  /**
   * A tap on a row plays it — except the row already held, which carries on from where it
   * is: a track playing is left alone, and a held one (a restored launch) starts at its second
   * instead of from zero.
   */
  function tapRow(position: number): void {
    const entry = entries[position];
    if (onPick !== undefined) {
      if (entry !== undefined) onPick(entry.track);
      return;
    }
    if (position !== index) {
      player().playAt(position);
      return;
    }
    if (!isSounding(usePlayer.getState().status)) player().toggle();
  }

  /** Where the dragged track would land, drawn as a line on that row's edge. */
  function dropEdge(position: number): 'before' | 'after' | undefined {
    if (dragging === undefined || position !== dragging.to) return undefined;
    const from = entries.findIndex((entry) => entry.key === dragging.key);
    if (dragging.to === from) return undefined;
    return dragging.to < from ? 'before' : 'after';
  }

  /* ── drawing ──────────────────────────────────────────────────────────────── */

  return (
    <>
      <div className="queueKeys">
        <button
          type="button"
          className="key"
          aria-pressed={shuffle}
          onClick={() => {
            useQueue.getState().toggleShuffle();
          }}
        >
          <ShuffleGlyph className="keyIcon" />
          <span>{SHUFFLE_LABEL}</span>
        </button>
        <button
          type="button"
          className="key"
          aria-pressed={repeat !== 'off'}
          aria-label={REPEAT_TEXT[repeat]}
          onClick={() => {
            useQueue.getState().cycleRepeat();
          }}
        >
          <RepeatGlyph className="keyIcon" mode={repeat} />
          <span>{REPEAT_KEY_TEXT[repeat]}</span>
        </button>
        <button
          type="button"
          className="key"
          aria-pressed={transition !== 'cut'}
          aria-label={TRANSITION_TEXT[transition]}
          onClick={() => {
            useQueue.getState().cycleTransition();
          }}
        >
          <FadeGlyph className="keyIcon" />
          <span>{TRANSITION_KEY_TEXT[transition]}</span>
        </button>
        {sleepAt === undefined ? (
          <button
            type="button"
            className="key"
            aria-expanded={choosingSleep}
            onClick={() => {
              setChoosingSleep((open) => !open);
            }}
          >
            <MoonGlyph className="keyIcon" />
            <span>{SLEEP_LABEL}</span>
          </button>
        ) : (
          <button
            type="button"
            className="key"
            aria-pressed="true"
            aria-label={sleepOffLabel(sleepLeft)}
            onClick={() => {
              player().setSleep(undefined);
            }}
          >
            <MoonGlyph className="keyIcon" />
            <span>
              {/* To the end of the track: what is left is the track's own, and the reader says it. */}
              {sleepAt === 'track'
                ? `${SLEEP_LABEL}: конец трека`
                : `${SLEEP_LABEL}${sleepLeft === undefined ? '' : ` ${sleepLeft}`}`}
            </span>
          </button>
        )}
        <button
          type="button"
          className="key"
          disabled={nothingToClear}
          onClick={() => {
            useQueue.getState().clear();
          }}
        >
          <TrashGlyph className="keyIcon" />
          <span>{CLEAR_LABEL}</span>
        </button>
      </div>
      {choosingSleep && sleepAt === undefined && (
        <div className="queueModes" role="group" aria-label={SLEEP_LABEL}>
          {SLEEP_CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              className="chip"
              onClick={() => {
                player().setSleep(choice);
                setChoosingSleep(false);
              }}
            >
              {SLEEP_CHOICE_TEXT[choice]}
            </button>
          ))}
        </div>
      )}
      {shuffle && <p className="queueNote">{SHUFFLE_NOTE_TEXT}</p>}

      {entries.length === 0 ? (
        <p className="queueNote">{EMPTY_QUEUE_TEXT}</p>
      ) : (
        <ol className="queueList" aria-label={QUEUE_LIST_LABEL}>
          {entries.map((entry, position) => {
            const { track } = entry;
            const isCurrent = position === index;
            const isDragged = dragging?.key === entry.key;
            // Task 71: «Сейчас» over the track playing, «Дальше» over the one after it.
            const label = isCurrent
              ? NOW_LABEL
              : index >= 0 && position === index + 1
                ? NEXT_LABEL_TEXT
                : undefined;
            return [
              label !== undefined && (
                <li key={`label-${label}`} className="queueLabel" role="presentation">
                  {label}
                </li>
              ),
              <li
                key={entry.key}
                ref={isCurrent ? currentRef : undefined}
                className="queueRow"
                data-current={isCurrent ? 'true' : undefined}
                data-picked={pickedId === track.id ? 'true' : undefined}
                data-dragging={isDragged ? 'true' : undefined}
                data-drop={dropEdge(position)}
                style={
                  isDragged ? { transform: `translateY(${String(dragging.offset)}px)` } : undefined
                }
              >
                <button
                  type="button"
                  className="queueHandle"
                  aria-label={moveLabel(track.title)}
                  onPointerDown={(event) => {
                    startDrag(event, entry.key, position);
                  }}
                  onPointerMove={moveDrag}
                  ref={(node) => {
                    if (node === null) handles.current.delete(entry.key);
                    else handles.current.set(entry.key, node);
                  }}
                  onPointerUp={(event) => {
                    endDrag(event.pointerId, true);
                  }}
                  onPointerCancel={(event) => {
                    endDrag(event.pointerId, false);
                  }}
                  onKeyDown={(event) => {
                    keyMove(event, entry.key, position);
                  }}
                >
                  <GrabGlyph className="queueIcon" />
                </button>
                <button
                  type="button"
                  className={isCurrent ? 'queueMain queueCurrent' : 'queueMain'}
                  aria-current={isCurrent ? 'true' : undefined}
                  onClick={() => {
                    tapRow(position);
                  }}
                >
                  <span className="queueText">
                    <span className="queueTitle">{track.title}</span>
                    {pinned.has(entry.key) ? (
                      <span className="queueByline queuePinned">
                        {PINNED_TEXT}
                        {track.performer !== '' ? ` · ${track.performer}` : ''}
                      </span>
                    ) : (
                      track.performer !== '' && (
                        <span className="queueByline">{track.performer}</span>
                      )
                    )}
                  </span>
                  {isCurrent && <LevelBars moving={playing} />}
                  <span className="queueDuration">{formatDuration(track.durationS)}</span>
                </button>
                {!isCurrent && (
                  <button
                    type="button"
                    className="queueRemove"
                    aria-label={removeLabel(track.title)}
                    onClick={() => {
                      useQueue.getState().remove(entry.key);
                    }}
                  >
                    <CloseGlyph className="queueIcon" />
                  </button>
                )}
              </li>,
            ];
          })}
        </ol>
      )}
    </>
  );
}
